import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { getFilesPerDay } from '@commons/getFilesPerDay';
import { Line } from 'react-chartjs-2';
import { Pie } from 'react-chartjs-2';
import { getInputTypeCount } from '@commons/getInputTypeCount';
import {
  Badge,
  Container,
  Title,
  Paper,
  Group,
  Button,
  Modal,
  TextInput,
  Loader,
  Flex,
  Table,
  Stack,
  Grid,
  Text,
  Center,
} from '@mantine/core';
import { IconSettings, IconFilePlus, IconEye } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '@store';
import { updateDashboard } from '@store/slices/dashboardSlice';
import routes from '@commons/routes';
import { isBetaBadgeVisible } from '@commons/betaBadge';
import { useTimeAgo } from '@commons/timeAgo';

import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  ArcElement,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
);

const TypePieChart = ({ data, title }: { data: any[]; title: string }) => {
  const intl = useIntl();
  const typeCounts = getInputTypeCount(data);
  const labels = Object.keys(typeCounts);
  const counts = Object.values(typeCounts);

  const chartData: any = {
    labels: labels.map((label: any) =>
      intl.formatMessage({ id: label, defaultMessage: label })
    ),
    datasets: [
      {
        label: 'Type Usage',
        data: counts,
        backgroundColor: [
          '#36A2EB',
          '#FF6384',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40',
        ],
        borderColor: '#ffffff',
        borderWidth: 1,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 8,
          font: {
            size: 11,
          },
          usePointStyle: true,
        },
        maxWidth: 400,
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem: any) {
            const value = counts[tooltipItem.dataIndex];
            const label = labels[tooltipItem.dataIndex];
            return `${intl.formatMessage({ id: label, defaultMessage: label })}: ${value}`;
          },
        },
      },
    },
  };

  return (
    <Paper p="md" radius="md" withBorder w="100%">
      <Title order={5} mb="md">
        {title}
      </Title>
      <div style={{ height: '200px', width: '100%' }}>
        <Pie data={chartData} options={options} />
      </div>
    </Paper>
  );
};

const FilesChart = ({ filesDates }: { filesDates: any[] }) => {
  const dailyFiles = getFilesPerDay(filesDates);
  const intl = useIntl();

  const data = {
    labels: dailyFiles.map((entry) => entry.date),
    datasets: [
      {
        label: intl.formatMessage({ id: 'filesPerDay' }),
        data: dailyFiles.map((entry: any) => entry.count),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.2,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: intl.formatMessage({ id: 'date' }),
        },
      },
      y: {
        title: {
          display: true,
          text: intl.formatMessage({ id: 'numberOfFiles' }),
        },
        beginAtZero: true,
      },
    },
  };

  return (
    <Paper p="md" radius="md" withBorder w="100%">
      <Title order={5} mb="md">
        {intl.formatMessage({ id: 'filesCreatedPerDay' })}
      </Title>
      <div style={{ height: '200px', width: '100%' }}>
        <Line data={data} options={options} />
      </div>
    </Paper>
  );
};

export const UserDashboard = () => {
  const intl = useIntl();
  const timeAgo = useTimeAgo();
  const { username } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { dashboard, isLoading: dashboardLoading } = useAppSelector(
    (state) => state.dashboard
  );

  const {
    files,
    isLoading: filesLoading,
    error: filesError,
  } = useAppSelector((state) => state.files);

  const { models, isLoading: modelsLoading } = useAppSelector(
    (state) => state.models
  );

  const { filters, isLoading: filtersLoading } = useAppSelector(
    (state) => state.filters
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (dashboard) {
      setCategory(dashboard.category || '');
    }
  }, [dashboard]);

  const handleOpenModal = () => {
    if (dashboard) {
      setCategory(dashboard.category || '');
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    if (dashboard) {
      setCategory(dashboard.category || '');
    }
  };

  const handleUpdateCategory = async () => {
    if (!username || !category.trim()) {
      return;
    }

    setIsUpdating(true);
    try {
      await dispatch(
        updateDashboard({ username, category: category.trim() })
      ).unwrap();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error updating dashboard category:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  if (filesError) {
    throw filesError;
  }

  // Filter models by dashboard category
  const filteredModels = dashboard?.category
    ? models.filter(
        (model) =>
          model.category === dashboard.category && model.type === 'CONTRACT'
      )
    : [];

  // Filter filters by dashboard category
  const filteredFilters = dashboard?.category
    ? filters.filter((filter) => filter.category === dashboard.category)
    : [];

  const modelRows = filteredModels.map((model) => (
    <Table.Tr key={model.id}>
      <Table.Td>{model.name}</Table.Td>
      <Table.Td ta="center">
        {intl.formatMessage({ id: model.type, defaultMessage: model.type })}
      </Table.Td>
      <Table.Td ta="center">{model.category}</Table.Td>
      <Table.Td ta="center">
        {timeAgo.format(new Date(model.updatedAt || model.createdAt))}
      </Table.Td>
      <Table.Td ta="center">
        <Button
          size="xs"
          variant="light"
          leftSection={<IconFilePlus size={16} />}
          onClick={() => {
            if (username) {
              navigate(routes.routes.createDocument(username, model.id));
            }
          }}
        >
          {intl.formatMessage({ id: 'create' })}
        </Button>
      </Table.Td>
    </Table.Tr>
  ));

  const filterRows = filteredFilters.map((filter) => (
    <Table.Tr key={filter.id}>
      <Table.Td>{filter.name}</Table.Td>
      <Table.Td ta="center">{filter.category}</Table.Td>
      <Table.Td ta="center">
        {timeAgo.format(new Date(filter.updatedAt || filter.createdAt))}
      </Table.Td>
      <Table.Td ta="center">
        <Button
          size="xs"
          variant="light"
          leftSection={<IconEye size={16} />}
          onClick={() => {
            if (username) {
              navigate(routes.routes.filters.view(username, filter.id));
            }
          }}
        >
          {intl.formatMessage({ id: 'open', defaultMessage: 'Open' })}
        </Button>
      </Table.Td>
    </Table.Tr>
  ));

  const queryFilterRows = filteredFilters.map((filter) => (
    <Table.Tr key={`query-${filter.id}`}>
      <Table.Td>{filter.name}</Table.Td>
      <Table.Td ta="center">{filter.category}</Table.Td>
      <Table.Td ta="center">
        {timeAgo.format(new Date(filter.updatedAt || filter.createdAt))}
      </Table.Td>
      <Table.Td ta="center">
        <Button
          size="xs"
          variant="light"
          leftSection={<IconEye size={16} />}
          onClick={() => {
            if (username) {
              navigate(routes.routes.queryFolders.view(username, filter.id));
            }
          }}
        >
          {intl.formatMessage({ id: 'open', defaultMessage: 'Open' })}
        </Button>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Container size="xl" mb={200}>
      <Flex justify="space-between" align="center" mt="lg" mb="md">
        <Title order={4}>{intl.formatMessage({ id: 'dashboard' })}</Title>
        <Button
          leftSection={<IconSettings size={16} />}
          variant="light"
          onClick={handleOpenModal}
          disabled={dashboardLoading || !dashboard}
        >
          {intl.formatMessage({ id: 'settings', defaultMessage: 'Settings' })}
        </Button>
      </Flex>

      <Stack gap="md">
        {dashboard?.category && (
          <Stack gap="md">
            <Group gap="sm" align="center" wrap="wrap">
              <Title order={5} mb={0}>
                {intl.formatMessage(
                  {
                    id: 'queryFoldersInCategory',
                  },
                  { category: dashboard.category }
                )}
              </Title>
              {isBetaBadgeVisible() ? (
                <Badge
                  size="xs"
                  variant="filled"
                  color="blue"
                  tt="uppercase"
                  radius="sm"
                >
                  {intl.formatMessage({ id: 'beta' })}
                </Badge>
              ) : null}
            </Group>
            <Text size="sm" c="dimmed">
              {intl.formatMessage({ id: 'queryFoldersListDescription' })}
            </Text>
            {filtersLoading || dashboardLoading ? (
              <Paper p="md" radius="md" withBorder>
                <Center h={250}>
                  <Loader />
                </Center>
              </Paper>
            ) : filteredFilters.length > 0 ? (
              <Table.ScrollContainer minWidth={800}>
                <Table
                  striped
                  highlightOnHover
                  withTableBorder
                  withColumnBorders
                  layout="auto"
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{intl.formatMessage({ id: 'name' })}</Table.Th>
                      <Table.Th ta="center" w={150}>
                        {intl.formatMessage({ id: 'category' })}
                      </Table.Th>
                      <Table.Th ta="center" w={150}>
                        {intl.formatMessage({ id: 'updatedAt' })}
                      </Table.Th>
                      <Table.Th ta="center" w={120}>
                        {intl.formatMessage({ id: 'actions' })}
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>{queryFilterRows}</Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ) : (
              <Paper p="md" radius="md" withBorder>
                <Flex justify="center" align="center" h={100}>
                  <Text c="dimmed">
                    {intl.formatMessage({
                      id: 'noFiltersInCategory',
                      defaultMessage: 'No filters in this category',
                    })}
                  </Text>
                </Flex>
              </Paper>
            )}
          </Stack>
        )}

        {dashboard?.category && (
          <Stack gap="md">
            <Title order={5}>
              {intl.formatMessage(
                {
                  id: 'filtersInCategory',
                  defaultMessage: 'Filters in category "{category}"',
                },
                { category: dashboard.category }
              )}
            </Title>
            <Text size="sm" c="dimmed">
              {intl.formatMessage({ id: 'dashboardFiltersTableDescription' })}
            </Text>
            {filtersLoading || dashboardLoading ? (
              <Paper p="md" radius="md" withBorder>
                <Center h={250}>
                  <Loader />
                </Center>
              </Paper>
            ) : filteredFilters.length > 0 ? (
              <Table.ScrollContainer minWidth={800}>
                <Table
                  striped
                  highlightOnHover
                  withTableBorder
                  withColumnBorders
                  layout="auto"
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{intl.formatMessage({ id: 'name' })}</Table.Th>
                      <Table.Th ta="center" w={150}>
                        {intl.formatMessage({ id: 'category' })}
                      </Table.Th>
                      <Table.Th ta="center" w={150}>
                        {intl.formatMessage({ id: 'updatedAt' })}
                      </Table.Th>
                      <Table.Th ta="center" w={120}>
                        {intl.formatMessage({ id: 'actions' })}
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>{filterRows}</Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ) : (
              <Paper p="md" radius="md" withBorder>
                <Flex justify="center" align="center" h={100}>
                  <Text c="dimmed">
                    {intl.formatMessage({
                      id: 'noFiltersInCategory',
                      defaultMessage: 'No filters in this category',
                    })}
                  </Text>
                </Flex>
              </Paper>
            )}
          </Stack>
        )}

        {dashboard?.category && (
          <Stack gap="md">
            <Title order={5}>
              {intl.formatMessage(
                {
                  id: 'modelsInCategory',
                  defaultMessage: 'Models in category "{category}"',
                },
                { category: dashboard.category }
              )}
            </Title>
            {modelsLoading || dashboardLoading ? (
              <Paper p="md" radius="md" withBorder>
                <Center h={250}>
                  <Loader />
                </Center>
              </Paper>
            ) : filteredModels.length > 0 ? (
              <Table.ScrollContainer minWidth={800}>
                <Table
                  striped
                  highlightOnHover
                  withTableBorder
                  withColumnBorders
                  layout="auto"
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{intl.formatMessage({ id: 'name' })}</Table.Th>
                      <Table.Th ta="center" w={150}>
                        {intl.formatMessage({ id: 'type' })}
                      </Table.Th>
                      <Table.Th ta="center" w={150}>
                        {intl.formatMessage({ id: 'category' })}
                      </Table.Th>
                      <Table.Th ta="center" w={150}>
                        {intl.formatMessage({ id: 'updatedAt' })}
                      </Table.Th>
                      <Table.Th ta="center" w={120}>
                        {intl.formatMessage({ id: 'actions' })}
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>{modelRows}</Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ) : (
              <Paper p="md" radius="md" withBorder>
                <Flex justify="center" align="center" h={100}>
                  <Text c="dimmed">
                    {intl.formatMessage({
                      id: 'noModelsInCategory',
                      defaultMessage: 'No models in this category',
                    })}
                  </Text>
                </Flex>
              </Paper>
            )}
          </Stack>
        )}

        <Grid>
          {filesLoading ? (
            <>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Paper p="md" radius="md" withBorder w="100%">
                  <Title order={5} mb="md">
                    {intl.formatMessage({ id: 'fileTypesDistribution' })}
                  </Title>
                  <Flex justify="center" align="center" h={200}>
                    <Loader />
                  </Flex>
                </Paper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Paper p="md" radius="md" withBorder w="100%">
                  <Title order={5} mb="md">
                    {intl.formatMessage({ id: 'fieldsDistribution' })}
                  </Title>
                  <Flex justify="center" align="center" h={200}>
                    <Loader />
                  </Flex>
                </Paper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                <Paper p="md" radius="md" withBorder w="100%">
                  <Title order={5} mb="md">
                    {intl.formatMessage({ id: 'filesCreatedPerDay' })}
                  </Title>
                  <Flex justify="center" align="center" h={200}>
                    <Loader />
                  </Flex>
                </Paper>
              </Grid.Col>
            </>
          ) : files && files.length > 0 ? (
            <>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <TypePieChart
                  title={intl.formatMessage({ id: 'fileTypesDistribution' })}
                  data={files.map((item) => ({ type: item.type }))}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <TypePieChart
                  title={intl.formatMessage({ id: 'fieldsDistribution' })}
                  data={files
                    .map((item) => item.fields)
                    .reduce((acc: any, curr: any) => {
                      if (acc.includes(curr)) {
                        return acc;
                      }
                      return [...acc, curr];
                    }, [])
                    .flat()
                    .map((field: any) => ({ type: field.type }))}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                <FilesChart filesDates={files.map((item) => item.createdAt)} />
              </Grid.Col>
            </>
          ) : (
            <Grid.Col span={12}>
              <Paper p="md" radius="md" withBorder>
                <Flex justify="center" align="center" h={200}>
                  <Text c="dimmed">
                    {intl.formatMessage({
                      id: 'noFilesAvailable',
                      defaultMessage: 'No files available',
                    })}
                  </Text>
                </Flex>
              </Paper>
            </Grid.Col>
          )}
        </Grid>
      </Stack>

      <Modal
        title={intl.formatMessage({
          id: 'updateDashboardCategory',
          defaultMessage: 'Update Dashboard Category',
        })}
        opened={isModalOpen}
        onClose={handleCloseModal}
        centered
        closeOnClickOutside={!isUpdating}
        closeOnEscape={!isUpdating}
      >
        <TextInput
          label={intl.formatMessage({
            id: 'category',
            defaultMessage: 'Category',
          })}
          value={category}
          onChange={(e) => setCategory(e.currentTarget.value)}
          disabled={isUpdating}
          placeholder={intl.formatMessage({
            id: 'enterCategory',
            defaultMessage: 'Enter category',
          })}
          mb="lg"
        />

        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={handleCloseModal}
            disabled={isUpdating}
          >
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
          <Button
            onClick={handleUpdateCategory}
            disabled={isUpdating || !category.trim()}
            leftSection={isUpdating ? <Loader size="xs" /> : undefined}
          >
            {intl.formatMessage({ id: 'update', defaultMessage: 'Update' })}
          </Button>
        </Group>
      </Modal>
    </Container>
  );
};
